import React, { useState, useEffect, useRef } from "react";
import {
  X,
  FileText,
  User,
  Clock,
  DollarSign,
  Building,
  MapPin,
  Loader2,
  Printer,
} from "lucide-react";
import * as XLSX from "xlsx";
import { useLocation, useNavigate } from "react-router-dom";
import MapViewModal from "./EmployeeMapTracker";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import EmployeeMapTracker from "../../src/pages/EmployeeMapTracker";
import { formatDate, formatDateTime } from "../utils/formatDate";
import { formatTimeTo12Hour } from "../utils/formatTime";
const PobDatewiseData = ({
  employeeId: propEmployeeId,
  date: propDate,
  onClose,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const qs = new URLSearchParams(location.search);

  // Priority: query params -> props
  const employeeIdFromQuery = qs.get("employee_id") || qs.get("employeeId");
  const dateFromQuery = qs.get("date");

  const employeeId = employeeIdFromQuery || propEmployeeId;
  const date = dateFromQuery || propDate;

  const printableRef = useRef(null);
  const [showMap, setShowMap] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapModal, setMapModal] = useState({
    isOpen: false,
    employeeData: null,
  });
  const closeMapModal = () => {
    setMapModal({ isOpen: false, employeeData: null });
  };
  // Fetch and normalize response shape
  useEffect(() => {
    const fetchData = async () => {
      if (!employeeId || !date) {
        setLoading(false);
        setError("Missing employee id or date");
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // normalize date to YYYY-MM-DD (strip any time part)
        const normalizedDate = new Date(date).toISOString().split("T")[0];

        const response = await axios.get(
          `${BASE_URL}/api/employees/${encodeURIComponent(
            employeeId
          )}/visit-full-summary?date=${encodeURIComponent(normalizedDate)}`,
          { headers: getAuthHeaders() }
        );

        console.log("API response:", response.data);

        const resp = response.data || {};

        // Try common shapes
        const employeeObj =
          resp.employee || resp.data?.employee || resp.data?.emp || resp;
        const summaryRaw =
          resp.summary ||
          resp.data?.summary ||
          resp.data?.visit_summary ||
          null;

        // Normalize summary to array so rest of component can use summary[0]
        const summaryArr = Array.isArray(summaryRaw)
          ? summaryRaw
          : summaryRaw
            ? [summaryRaw]
            : [];

        setData({
          employee: employeeObj || { name: "-" },
          summary: summaryArr,
        });
      } catch (err) {
        console.error("Error fetching visit details", err);
        setError("Failed to fetch visit details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    // reset state while loading new data
    setData(null);
    setError(null);
    setLoading(true);

    fetchData();
  }, [employeeId, date]);

  const formatCurrency = (value) => {
    return value || value === 0
      ? `₹${Number.parseFloat(value).toFixed(2)}`
      : "₹0.00";
  };

  const safeNum = (v) =>
    Number.isFinite(Number(v)) ? Number.parseFloat(v) : 0;

  const totals = (dayData) => {
    const totals = {
      pob: 0,
      sob: 0,
      expenseTotal: 0,
    };

    (dayData.doctors || []).forEach((d) => (totals.pob += safeNum(d.pob)));
    (dayData.chemists || []).forEach((d) => (totals.pob += safeNum(d.pob)));
    (dayData.stockists || []).forEach((d) => (totals.sob += safeNum(d.sob)));
    (dayData.distributors || []).forEach((d) => (totals.sob += safeNum(d.sob)));

    totals.expenseTotal =
      safeNum(dayData.expense?.total) ||
      safeNum(dayData.expense?.ta) +
      safeNum(dayData.expense?.da_hq) +
      safeNum(dayData.expense?.da_exstation) +
      safeNum(dayData.expense?.da_outstation) +
      safeNum(dayData.expense?.hotel_charges) +
      safeNum(dayData.expense?.other_charges);

    return totals;
  };

  const formatRemarksWithStockist = (item) => {
    return (
      [item?.remarks, item?.working_with_stockist]
        .filter(Boolean)
        .join(" - ") || "-"
    );
  };

  // ✅ Dynamic and fully data-driven map modal trigger
  const handleViewLocation = (type, item) => {
    try {
      if (!item) return;

      const hasCoords =
        (item.latitude && item.longitude) ||
        (item.coordinates && item.coordinates.lat && item.coordinates.lng);

      const lat = hasCoords
        ? Number(item.latitude || item.coordinates.lat)
        : 28.6139 + Math.random() * 0.01;

      const lng = hasCoords
        ? Number(item.longitude || item.coordinates.lng)
        : 77.209 + Math.random() * 0.01;

      const locObj = {
        type,
        name: item.name || item.retailer || "Unknown",
        visit_id: item.visit_id, // <-- add this

        coordinates: { lat, lng },
        time: item.time || item.filled_date || "-",
        remarks: item.remarks || "-",
        raw: item,
      };

      // dynamically set the selected location and modal data
      setSelectedLocation(locObj);
      setShowMap(true);

      // dynamically open modal with live employee + date + location info
      setMapModal({
        isOpen: true,
        employeeData: {
          employeeId: data?.employee?._id || data?.employee?.employee_id || "",
          employeeName: data?.employee?.name || "-",
          trackDate: date,
          visitId: item.visit_id,
          context: { from: "pobdatewise", type },
          location: locObj,
        },
      });
    } catch (err) {
      console.error("Error while opening map modal:", err);
    }
  };

  const handleExportToExcel = () => {
    try {
      if (!data) return;

      const workbook = XLSX.utils.book_new();
      const { employee, summary } = data;
      const dayData = summary[0] || {};

      // Employee Info sheet
      const employeeData = [
        [
          "HeadQuarter",
          "Working Date",
          "Filled Date",
          "Route In TP",
          "Route Covered",
          "Area Covered",
          "Remarks Of Day",
          "City",
          "State",
          "Routes (List)",
          "Total Routes Count",
        ],
        [
          employee?.headquarter || "-",
          dayData.date || "-",
          dayData.filled_time || "-",
          dayData.route_in_tp || "-",
          Array.isArray(dayData.routes)
            ? dayData.routes.join(", ")
            : dayData.routes || "-",
          dayData.area_covered || "-",
          dayData.remarks || "-",
          employee?.city || "-",
          employee?.state || "-",
          Array.isArray(employee?.routes)
            ? employee.routes.join(", ")
            : employee?.routes || "-",
          Array.isArray(employee?.routes)
            ? employee.routes.length
            : employee?.routes
              ? 1
              : 0,
        ],
      ];
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.aoa_to_sheet(employeeData),
        "Employee Info"
      );

      // Doctors
      if (Array.isArray(dayData.doctors) && dayData.doctors.length > 0) {
        const doctorsData = [
          [
            "S.No",
            "Doctor",
            "Area",
            "Route",
            "Working With",
            "Time",
            "Server Time",
            "Filled",
            "POB",
            "Doctor Remarks",
          ],
          ...dayData.doctors.map((doc, idx) => [
            idx + 1,
            doc.name || "-",
            doc.area || "-",
            doc.route || "-",
            doc.working_with || "Individual",
            doc.time || "-",
            doc.filled_date || "-",
            doc.filled || "Mobile",
            Number.parseFloat(doc.pob) || 0,
            formatRemarksWithStockist(doc)
          ]),
          [
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "Total",
            dayData.doctors.reduce(
              (sum, d) => sum + (Number.parseFloat(d.pob) || 0),
              0
            ),
            "",
          ],
        ];
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.aoa_to_sheet(doctorsData),
          "Doctors Visited"
        );
      }

      // Chemists
      if (Array.isArray(dayData.chemists) && dayData.chemists.length > 0) {
        const chemistsData = [
          [
            "S.No",
            "Chemist",
            "Area",
            "Working With",
            "Time",
            "Server Time",
            "Filled",
            "POB",
            "Chemist Remarks",
          ],
          ...dayData.chemists.map((chem, idx) => [
            idx + 1,
            chem.name || "-",
            chem.area || "-",
            chem.working_with || "Individual",
            chem.time || "-",
            chem.filled_date || "-",
            chem.filled || "Mobile",
            Number.parseFloat(chem.pob) || 0,
            formatRemarksWithStockist(chem)
          ]),
          [
            "",
            "",
            "",
            "",
            "",
            "",
            "Total",
            dayData.chemists.reduce(
              (sum, c) => sum + (Number.parseFloat(c.pob) || 0),
              0
            ),
            "",
          ],
        ];
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.aoa_to_sheet(chemistsData),
          "Chemists Visited"
        );
      }

      // Stockists
      if (Array.isArray(dayData.stockists) && dayData.stockists.length > 0) {
        const stockistsData = [
          [
            "S.No",
            "Stockist",
            "City",
            "Working With",
            "Time",
            "Server Time",
            "SOB",
            "Remarks",
          ],
          ...dayData.stockists.map((stk, idx) => [
            idx + 1,
            stk.name || "-",
            stk.city || "-",
            stk.working_with || "Individual",
            stk.time || "-",
            stk.server_time || "-",
            Number.parseFloat(stk.sob) || 0,
            formatRemarksWithStockist(stk)
          ]),
        ];
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.aoa_to_sheet(stockistsData),
          "Stockists Visited"
        );
      }

      // Distributors
      if (
        Array.isArray(dayData.distributors) &&
        dayData.distributors.length > 0
      ) {
        const distributorsData = [
          [
            "S.No",
            "Distributor",
            "City",
            "Working With",
            "Time",
            "Server Time",
            "SOB",
            "Remarks",
          ],
          ...dayData.distributors.map((dist, idx) => [
            idx + 1,
            dist.name || "-",
            dist.city || "-",
            dist.working_with || "Individual",
            dist.time || "-",
            dist.server_time || "-",
            Number.parseFloat(dist.sob) || 0,
            formatRemarksWithStockist(dist)
          ]),
        ];
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.aoa_to_sheet(distributorsData),
          "Distributors Visited"
        );
      }

      // Expense
      const expenseData = [
        [
          "S.No",
          "Route",
          "Type",
          "Distance",
          "TA",
          "DA (HQ)",
          "DA (Ex-Station)",
          "DA (Out-Station)",
          "Hotel Charges",
          "Other Charges",
          "Total",
          "Remarks",
        ],
        [
          1,
          dayData.expense?.route ||
          (Array.isArray(dayData.routes)
            ? dayData.routes.join(", ")
            : dayData.routes) ||
          "-",
          dayData.expense?.type || "Ex Station",
          Number.parseFloat(dayData.expense?.distance) || 0,
          Number.parseFloat(dayData.expense?.ta) || 0,
          Number.parseFloat(dayData.expense?.da_hq) || 0,
          Number.parseFloat(dayData.expense?.da_exstation) || 0,
          Number.parseFloat(dayData.expense?.da_outstation) || 0,
          Number.parseFloat(dayData.expense?.hotel_charges) || 0,
          Number.parseFloat(dayData.expense?.other_charges) || 0,
          Number.parseFloat(dayData.expense?.total) || 0,
          dayData.expense?.remarks || "-",
        ],
      ];
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.aoa_to_sheet(expenseData),
        "Expense Details"
      );

      const fileName = `DCR_${(employee?.name || "Employee").replace(
        /\s+/g,
        "_"
      )}_${dayData.date || new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } catch (err) {
      console.error("Error exporting to Excel:", err);
      alert("Error exporting to Excel. Please try again.");
    }
  };

  const handlePrint = () => {
    if (!printableRef.current) return;

    const printWindow = window.open("", "_blank", "width=900,height=700");
    if (!printWindow) return;

    const doc = printWindow.document;
    doc.open();
    doc.write(`<!doctype html><html><head><title>Print DCR</title><meta name="viewport" content="width=device-width,initial-scale=1" /><style>
      body{font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial; padding:20px; color:#111}
      table{border-collapse:collapse; width:100%}
      th,td{border:1px solid #e5e7eb; padding:8px; font-size:12px}
      th{background:#f3f4f6; font-weight:600}
    </style></head><body>`);

    // Clone printable content and strip interactive elements
    const clone = printableRef.current.cloneNode(true);
    // remove buttons from clone if any
    clone.querySelectorAll("button").forEach((b) => b.remove());

    doc.body.appendChild(clone);
    doc.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      // optionally close after print
      // printWindow.close();
    }, 300);
  };

  const handleReturn = () => {
    navigate("/unlock-dcr");
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <p className="mt-2 text-gray-600">Loading visit details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4">
        <div className="bg-red-100 text-red-700 p-4 -lg max-w-md text-center">
          <p>{error}</p>
          <div className="mt-4 flex justify-center gap-2">
            <button
              onClick={handleReturn}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 "
            >
              Return
            </button>
            <button
              onClick={onClose}
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 "
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <p className="text-gray-500">No data available</p>
        <div className="mt-4 flex gap-2">
          <button
            onClick={handleReturn}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 "
          >
            Return
          </button>
          <button
            onClick={onClose}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 "
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const { employee, summary } = data;
  const dayData = summary[0] || {};
  const summed = totals(dayData);
  // Only doctors' POB total for the Doctors table totals row
  const doctorTotal = Array.isArray(dayData.doctors)
    ? dayData.doctors.reduce(
      (sum, d) => sum + (Number.parseFloat(d.pob) || 0),
      0
    )
    : 0;
  const chemistTotal = Array.isArray(dayData.chemists)
    ? dayData.chemists.reduce(
      (sum, c) => sum + (Number.parseFloat(c.pob) || 0),
      0
    )
    : 0;
  return (
    <div className="flex flex-col h-full bg-white -lg shadow-2xl overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b-2 border-gray-200 p-4">
        <div className="flex justify-center items-center">
          <div className="text-center">
            <h1 className="text-xl font-bold text-gray-800 mb-1">
              DCR Preview - {employee?.name || "Employee"} (
              {employee?.designation || "-"}) {employee?.headquarter || "-"}
            </h1>
            <p className="text-sm text-gray-600">
              <span className="font-bold">Date:</span>{" "}
              {dayData?.date || date || "-"}
            </p>
          </div>
        </div>
      </div>

      {/* Content (printableRef wraps everything we want available for print) */}
      <div ref={printableRef} className="flex-1 overflow-y-auto bg-gray-50 p-6">
        <div className="space-y-6">
          {/* Employee Info */}
          <section className="bg-white -xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <User className="h-5 w-5 text-indigo-600" /> Employee
                Information
              </h2>
            </div>
            <div className="overflow-x-auto">
              <div className="overflow-x-auto">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-blue-600 sticky top-0 z-10 shadow-sm text-white">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold ">
                          State
                        </th>
                        <th className="px-4 py-3 text-left font-semibold ">
                          City
                        </th>
                        <th className="border-r border-gray-200 px-4 py-3 text-left font-semibold ">
                          HeadQuarter
                        </th>
                        <th className="border-r border-gray-200 px-4 py-3 text-left font-semibold ">
                          Route In TP
                        </th>
                        <th className="border-r border-gray-200 px-4 py-3 text-left font-semibold ">
                          Route Covered
                        </th>
                        <th className="border-r border-gray-200 px-4 py-3 text-left font-semibold ">
                          Area Covered
                        </th>
                        <th className="border-r border-gray-200 px-4 py-3 text-left font-semibold ">
                          Remarks Of Day
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="hover:bg-gray-50">
                        <td className="border-r border-gray-200 px-4 py-3">
                          {employee?.state || "-"}
                        </td>
                        <td className="border-r border-gray-200 px-4 py-3">
                          {employee?.city || "-"}
                        </td>
                        <td className="border-r border-gray-200 px-4 py-3">
                          {employee?.headquarter || "-"}
                        </td>
                        <td className="border-r border-gray-200 px-4 py-3">
                          {dayData?.route_in_tp || "-"}
                        </td>
                        <td className="border-r border-gray-200 px-4 py-3">
                          {Array.isArray(dayData?.route_covered) &&
                            dayData.route_covered.length > 0 ? (
                            <ol className="list-decimal pl-5 space-y-1">
                              {dayData.route_covered
                                .map((r) => (typeof r === "string" ? r : r?.name ?? ""))
                                .filter(Boolean)
                                .map((route, index) => (
                                  <li key={index}>{route}</li>
                                ))}
                            </ol>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="border-r border-gray-200 px-4 py-3">
                          {Array.isArray(dayData?.area_covered) &&
                            dayData.area_covered.length > 0
                            ? dayData.area_covered.join(", ")
                            : "-"}
                        </td>
                        <td className="border-r border-gray-200 px-4 py-3">
                          {dayData?.remarks || "-"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>
          {/* Doctors */}
          <section className="bg-white shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <User className="h-5 w-5 text-green-600" /> Doctors Visited (
                {(dayData.doctors && dayData.doctors.length) || 0})
              </h3>
            </div>
            {Array.isArray(dayData.doctors) && dayData.doctors.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm table-auto">
                  <thead className="bg-blue-600 sticky top-0 z-10 shadow-sm text-white">
                    <tr>
                      <th className="border-r border-gray-200 px-3 py-2">
                        S.No
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        Doctor
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        Area
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        Route
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        Working With
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        Time
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        Server Time
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        Filled
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        POB
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        Doctor Remarks
                      </th>
                      <th className="px-3 py-2">Map</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dayData.doctors.map((doctor, idx) => (
                      <tr
                        key={idx}
                        className={`hover:bg-gray-50 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                          }`}
                      >
                        <td className="border-r border-gray-200 px-3 py-2 text-center font-medium">
                          {idx + 1}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2 font-medium text-gray-900">
                          {doctor.name || "-"}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2">
                          {doctor.area || "-"}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2">
                          {doctor.route || "-"}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2">
                          {doctor.working_with || "Individual"}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2">
                          {formatTimeTo12Hour(doctor.time) || ""}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2">
                          {formatDateTime(doctor.filled_date) || "-"}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2">
                          {doctor.filled || "Mobile"}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2 font-semibold text-green-600">
                          {doctor.pob || "-"}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2">
                          {formatRemarksWithStockist(doctor)}
                        </td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => handleViewLocation("doctor", doctor)}
                            className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-2 py-1  text-xs font-medium transition-colors flex items-center gap-1"
                          >
                            <MapPin className="h-3 w-3" /> View
                          </button>
                        </td>
                      </tr>
                    ))}

                    {/* Totals row - doctors only POB */}
                    <tr className="bg-gray-100 font-semibold">
                      <td colSpan={8} className="px-3 py-2 text-right">
                        Total POB
                      </td>
                      <td className="px-3 py-2">{doctorTotal}</td>
                      <td colSpan={2}></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center">
                <User className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500 font-medium">No Doctor Visited</p>
              </div>
            )}
          </section>
          <section className="bg-white -xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Building className="h-5 w-5 text-teal-600" /> Chemists Visited
                ({dayData.chemists?.length || 0})
              </h3>
            </div>
            {Array.isArray(dayData.chemists) && dayData.chemists.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm table-auto">
                  <thead className="bg-blue-600 sticky top-0 z-10 shadow-sm text-white">
                    <tr>
                      <th className="border-r border-gray-200 px-3 py-2">
                        S.No
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        Chemist
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        Area
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        Working With
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        Time
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        Server Time
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        Filled
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        POB
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        Remarks
                      </th>
                      <th className="px-3 py-2">Map</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dayData.chemists.map((chemist, idx) => (
                      <tr
                        key={idx}
                        className={`hover:bg-gray-50 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                          }`}
                      >
                        <td className="border-r border-gray-200 px-3 py-2 text-center font-medium">
                          {idx + 1}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2">
                          {chemist.name || "-"}
                        </td>

                        <td className="border-r border-gray-200 px-3 py-2">
                          {chemist.area || "-"}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2">
                          {chemist.working_with || "Individual"}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2">
                          {formatTimeTo12Hour(chemist.time) || "-"}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2">
                          {formatDateTime(chemist.filled_date) || "-"}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2">
                          {chemist.filled || "Mobile"}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2 font-semibold text-teal-600">
                          {chemist.pob || "-"}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2">
                          {formatRemarksWithStockist(chemist)}
                        </td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() =>
                              handleViewLocation("chemist", chemist)
                            }
                            className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-2 py-1  text-xs font-medium transition-colors flex items-center gap-1"
                          >
                            <MapPin className="h-3 w-3" /> View
                          </button>
                        </td>
                      </tr>
                    ))}

                    <tr className="bg-gray-100 font-semibold">
                      <td colSpan={7} className="px-3 py-2 text-right">
                        Total POB
                      </td>
                      <td className="px-3 py-2">{chemistTotal}</td>
                      <td colSpan={2}></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center">
                <Building className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500 font-medium">No Chemist Visited</p>
              </div>
            )}
          </section>

          {/* Stockists */}
          <section className="bg-white -xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Building className="h-5 w-5 text-yellow-600" /> Stockists
                Visited ({dayData.stockists?.length || 0})
              </h3>
            </div>
            {Array.isArray(dayData.stockists) &&
              dayData.stockists.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm table-auto">
                  <thead className="bg-blue-600 sticky top-0 z-10 shadow-sm text-white">
                    <tr>
                      <th className="border-r border-gray-200 px-3 py-2">
                        S.No
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        Stockist
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        City
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        Working With
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        Time
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        Server Time
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        SOB
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        Remarks
                      </th>
                      <th className="px-3 py-2">Map</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dayData.stockists.map((stockist, idx) => (
                      <tr
                        key={idx}
                        className={`hover:bg-gray-50 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                          }`}
                      >
                        <td className="border-r border-gray-200 px-3 py-2 text-center font-medium">
                          {idx + 1}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2 font-medium text-gray-900">
                          {stockist.name || "-"}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2">
                          {stockist.city || "-"}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2">
                          {stockist.working_with || "Individual"}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2">
                         {formatTimeTo12Hour(stockist.time) || "-"}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2">
                          {formatDateTime(stockist.filled_date) || "-"}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2 font-semibold text-yellow-600">
                          {stockist.sob || "-"}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2">
                          {formatRemarksWithStockist(stockist)}
                        </td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() =>
                              handleViewLocation("stockist", stockist)
                            }
                            className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-2 py-1  text-xs font-medium transition-colors flex items-center gap-1"
                          >
                            <MapPin className="h-3 w-3" /> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center">
                <Building className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500 font-medium">No Stockist Visited</p>
              </div>
            )}
          </section>

          {/* Distributors */}
          <section className="bg-white -xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Building className="h-5 w-5 text-purple-600" /> Distributor
                Visited ({dayData.distributors?.length || 0})
              </h3>
            </div>
            {Array.isArray(dayData.distributors) &&
              dayData.distributors.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm table-auto">
                  <thead className="bg-blue-600 sticky top-0 z-10 shadow-sm text-white">
                    <tr>
                      <th className="border-r border-gray-200 px-3 py-2">
                        S.No
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        Distributor
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        City
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        Working With
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        Time
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        Server Time
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        SOB
                      </th>
                      <th className="border-r border-gray-200 px-3 py-2">
                        Remarks
                      </th>
                      <th className="px-3 py-2">Map</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dayData.distributors.map((distributor, idx) => (
                      <tr
                        key={idx}
                        className={`hover:bg-gray-50 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                          }`}
                      >
                        <td className="border-r border-gray-200 px-3 py-2 text-center font-medium">
                          {idx + 1}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2 font-medium text-gray-900">
                          {distributor.name || "-"}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2">
                          {distributor.city || "-"}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2">
                          {distributor.working_with || "Individual"}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2">
                          {formatTimeTo12Hour(distributor.time) || "-"}

                        </td>
                        <td className="border-r border-gray-200 px-3 py-2">
                          {formatDateTime(distributor.filled_date) || "-"}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2 font-semibold text-purple-600">
                          {distributor.sob || "-"}
                        </td>
                        <td className="border-r border-gray-200 px-3 py-2">
                          {formatRemarksWithStockist(distributor)}
                        </td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() =>
                              handleViewLocation("distributor", distributor)
                            }
                            className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-2 py-1  text-xs font-medium transition-colors flex items-center gap-1"
                          >
                            <MapPin className="h-3 w-3" /> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center">
                <Building className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500 font-medium">
                  No Distributor Visited
                </p>
              </div>
            )}
          </section>

          {/* Expense */}
          {/* Expense */}
          <section className="bg-white -xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-red-600" /> Expense Details
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-blue-600 sticky top-0 z-10 shadow-sm text-white">
                  <tr>
                    <th className="border-r border-gray-200 px-3 py-2">S.No</th>
                    <th className="border-r border-gray-200 px-3 py-2">
                      Route
                    </th>
                    <th className="border-r border-gray-200 px-3 py-2">Type</th>
                    <th className="border-r border-gray-200 px-3 py-2">
                      Distance
                    </th>
                    <th className="border-r border-gray-200 px-3 py-2">TA</th>
                    <th className="border-r border-gray-200 px-3 py-2">
                      DA (HQ)
                    </th>
                    <th className="border-r border-gray-200 px-3 py-2">
                      DA (Ex-Station)
                    </th>
                    <th className="border-r border-gray-200 px-3 py-2">
                      DA (Out-Station)
                    </th>
                    <th className="border-r border-gray-200 px-3 py-2">
                      Hotel Charges
                    </th>
                    <th className="border-r border-gray-200 px-3 py-2">
                      Other Charges
                    </th>
                    <th className="border-r border-gray-200 px-3 py-2">
                      Total
                    </th>
                    <th className="px-3 py-2">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="hover:bg-gray-50">
                    <td className="border-r border-gray-200 px-3 py-2 text-center font-medium">
                      1
                    </td>

                    {/* Route */}
                    <td className="border-r border-gray-200 px-3 py-2">
                      {dayData.expense?.route_name ??
                        (Array.isArray(dayData.routes)
                          ? dayData.routes.join(", ")
                          : dayData.routes) ??
                        "-"}
                    </td>

                    {/* Type (use route_type_name or trip_type depending on what you want) */}
                    <td className="border-r border-gray-200 px-3 py-2">
                      {dayData.expense?.route_type_name ??
                        dayData.expense?.trip_type ??
                        "-"}
                    </td>

                    {/* Numeric fields - show two decimals and treat null/undefined as 0.00 */}
                    <td className="border-r border-gray-200 px-3 py-2">
                      {Number(dayData.expense?.distance ?? 0).toFixed(2)}
                    </td>

                    <td className="border-r border-gray-200 px-3 py-2">
                      {Number(dayData.expense?.ta ?? 0).toFixed(2)}
                    </td>

                    <td className="border-r border-gray-200 px-3 py-2">
                      {Number(dayData.expense?.da_hq ?? 0).toFixed(2)}
                    </td>

                    <td className="border-r border-gray-200 px-3 py-2">
                      {Number(dayData.expense?.da_exstation ?? 0).toFixed(2)}
                    </td>

                    <td className="border-r border-gray-200 px-3 py-2">
                      {Number(dayData.expense?.da_outstation ?? 0).toFixed(2)}
                    </td>

                    <td className="border-r border-gray-200 px-3 py-2">
                      {Number(dayData.expense?.hotel_charges ?? 0).toFixed(2)}
                    </td>

                    <td className="border-r border-gray-200 px-3 py-2">
                      {Number(dayData.expense?.other_charges ?? 0).toFixed(2)}
                    </td>
                    <td className="border-r border-gray-200 px-3 py-2">
                      {dayData.expense?.total ?? "0.00"}
                    </td>
                    <td className="px-3 py-2">
                      {dayData.expense?.remarks || "-"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-t border-gray-200 flex justify-between items-center">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Clock className="h-4 w-4" />
          <span>Generated on {new Date().toLocaleDateString()}</span>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handlePrint}
            className="bg-white border border-gray-200 hover:bg-gray-50 px-4 py-2  flex items-center gap-2"
          >
            <Printer className="h-4 w-4" /> Print
          </button>
          <button
            onClick={handleExportToExcel}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2  flex items-center gap-2"
          >
            <FileText className="h-4 w-4" /> Export
          </button>
        </div>
      </div>
      {/* ✅ Dynamic Map Modal */}
      {mapModal.isOpen && mapModal.employeeData && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-2">
          <div className="bg-white -lg w-full max-w-7xl h-[95vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b bg-white sticky top-0 z-10">
              <h3 className="text-xl font-bold text-gray-800">
                Employee Location Tracker - {mapModal.employeeData.employeeName}
              </h3>
              <button
                onClick={closeMapModal}
                className="text-gray-500 hover: text-3xl font-bold bg-gray-100 hover:bg-gray-200 w-10 h-10 -full flex items-center justify-center"
              >
                ×
              </button>
            </div>

            <div className="flex-1 p-1 overflow-hidden">
              <EmployeeMapTracker
                key={`${mapModal.employeeData.employeeId}-${mapModal.employeeData.trackDate}`}
                employeeId={mapModal.employeeData.employeeId}
                trackDate={mapModal.employeeData.trackDate}
                visitId={mapModal.employeeData.visitId}
                employeeName={mapModal.employeeData.employeeName}
                context={mapModal.employeeData.context}
                location={mapModal.employeeData.location}
                onClose={closeMapModal}
                isModal={true}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PobDatewiseData;
